import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  DropdownItem,
  DropdownTrigger,
  Dropdown,
  DropdownMenu,
  Avatar,
  Button,
} from '@heroui/react';
import { Breadcrumbs, BreadcrumbItem } from '@heroui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { Icon, StandardIcon } from './common/icon';
import { useAppBarState } from '@/hooks/appbar';
import { useNavigation } from '@/hooks/navigation';
import { PeopleQuickSearch } from './common/input/quicksearch';
import { getInitials } from '@/shared/lib/names';
import { useCurrentUser } from '@/store/users/hooks';
import { tabIcons } from '@/lib/navigation';

export default function AppBar() {
  const navigate = useNavigate();
  const { firebaseUser, logout } = useAuth();
  const user = useCurrentUser();
  const { breadcrumbs } = useAppBarState();
  const { activeTab, visibleTabs } = useNavigation();
  const photoUrl = firebaseUser?.photoURL || undefined;
  const initials = getInitials(user?.data.displayName || '');

  const tabKeyToPath: Record<string, string> = {
    dashboard: '/',
    people: '/people',
    structure: '/structure',
    access: '/access',
    ministries: '/ministries',
    locations: '/locations',
    users: '/users',
    settings: '/settings',
  };

  const handleTabChange = (key: string) => {
    const targetPath = tabKeyToPath[key] || '/';
    navigate(targetPath);
  };

  if (!firebaseUser) {
    return null;
  }

  const signOut = () => {
    logout();
  };

  const maintenanceBannerEnabled = false;

  return (
    <>
      {maintenanceBannerEnabled && (
        <Navbar className='bg-warning-500' height='3rem'>
          <div className='flex flex-row items-center'>
            <StandardIcon id='mdi:alert-circle-outline' className='mr-2' />
            The system will be unavailable due to Scheduled Maintenance on 5/20
            from 9pm PDT to 10pm PDT.
          </div>
        </Navbar>
      )}
      <Navbar maxWidth='full' height={'4rem'} className=''>
        <NavbarBrand className='max-w-[200px] basis-1/3'>
          <img
            src='/ministryhub-black.svg?v=20260911'
            alt='Ministry Hub logo'
            width={160}
            height={46.8}
          />
        </NavbarBrand>
        <NavbarContent className='md:hidden'>
          {visibleTabs.length > 0 && (
            <Dropdown>
              <DropdownTrigger>
                <Button variant='light' isIconOnly aria-label='Navigation'>
                  <StandardIcon
                    id={tabIcons[activeTab] || 'mdi:folder-outline'}
                  />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label='Navigation sections'
                selectionMode='single'
                selectedKeys={new Set([activeTab])}
                onAction={(key) => handleTabChange(String(key))}
              >
                {visibleTabs.map((t) => (
                  <DropdownItem
                    key={t.key}
                    startContent={
                      <StandardIcon
                        id={tabIcons[t.key] || 'mdi:folder-outline'}
                      />
                    }
                  >
                    {t.title}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          )}
        </NavbarContent>
        <NavbarContent className='hidden gap-4 sm:flex' justify='center'>
          <NavbarItem>
            <Breadcrumbs>
              {breadcrumbs.map((breadcrumb, index) => (
                <BreadcrumbItem key={index}>{breadcrumb.label}</BreadcrumbItem>
              ))}
            </Breadcrumbs>
          </NavbarItem>
        </NavbarContent>
        <NavbarContent>
          <div className='flex-grow sm:hidden' />
          <PeopleQuickSearch />
        </NavbarContent>
        <NavbarContent justify='end' className='max-w-12'>
          <Dropdown placement='bottom-end'>
            <DropdownTrigger>
              {photoUrl ? (
                <Avatar
                  as='button'
                  className='transition-transform'
                  color='primary'
                  name={user?.data.displayName || undefined}
                  size='sm'
                  src={photoUrl}
                />
              ) : (
                <Avatar
                  as='button'
                  className='transition-transform'
                  color='primary'
                  name={user?.data.displayName || undefined}
                  size='md'
                  showFallback
                  getInitials={() => initials}
                />
              )}
            </DropdownTrigger>
            <DropdownMenu
              className='w-[340px]'
              itemClasses={{
                base: 'gap-4',
              }}
            >
              <DropdownItem
                key='autoscaling'
                startContent={<Icon icon='mdi:logout' />}
                onPress={signOut}
              >
                Sign Out
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarContent>
      </Navbar>
    </>
  );
}
